import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Typography, Alert, Box, Chip
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Edit } from '@mui/icons-material';
import { updateTimestamp } from '../store/sampleSlice';
import { formatToIST } from '../utils/dateUtils';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const TimestampEditor = ({ sample, onUpdate }) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const { showAllDates } = useSelector(state => state.settings);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newTimestamp, setNewTimestamp] = useState(null);
  const [error, setError] = useState('');

  if (user?.role !== 'Admin' || !showAllDates) {
    return null;
  }

  const formatDateTime = (date) => {
    return dayjs(date).tz('Asia/Kolkata');
  };

  const handleEdit = (type, item, field = null) => {
    setSelectedItem({ type, item, field });
    let currentTimestamp;
    
    if (type === 'created') {
      currentTimestamp = sample.createdDate;
    } else if (type === 'history') {
      currentTimestamp = item.at;
    } else if (type === 'department') {
      currentTimestamp = item[field];
    }
    
    setNewTimestamp(formatDateTime(currentTimestamp));
    setEditDialog(true);
    setError('');
  };

  const handleUpdate = async () => {
    if (!newTimestamp || !newTimestamp.isValid()) {
      setError('Please select a date and time');
      return;
    }

    try {
      const updateData = {
        id: sample._id,
        type: selectedItem.type,
        newTimestamp: newTimestamp.utc().toISOString()
      };

      if (selectedItem.type === 'history') {
        updateData.itemId = selectedItem.item._id;
      } else if (selectedItem.type === 'department') {
        updateData.itemId = selectedItem.item._id;
        updateData.field = selectedItem.field;
      }

      await dispatch(updateTimestamp(updateData)).unwrap();
      setEditDialog(false);
      setSelectedItem(null);
      setNewTimestamp(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      setError(error.message || 'Failed to update timestamp');
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Edit sx={{ mr: 1 }} /> Admin Timestamp Editor
      </Typography>

      {/* Created Date */}
      <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Sample Created</Typography>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEdit('created', null)}
          >
            Edit
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {formatToIST(sample.createdDate)}
        </Typography>
      </Box>

      {/* Department Assignments */}
      {sample.departmentAssignments?.map((dept, index) => (
        <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {dept.department} Department
          </Typography>
          
          {dept.hodAssignedAt && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">HOD Assigned: {formatToIST(dept.hodAssignedAt)}</Typography>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleEdit('department', dept, 'hodAssignedAt')}
              >
                Edit
              </Button>
            </Box>
          )}
          
          {dept.hodAcceptedAt && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">HOD Accepted: {formatToIST(dept.hodAcceptedAt)}</Typography>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleEdit('department', dept, 'hodAcceptedAt')}
              >
                Edit
              </Button>
            </Box>
          )}
          
          {dept.analystAssignedAt && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">Analyst Assigned: {formatToIST(dept.analystAssignedAt)}</Typography>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => handleEdit('department', dept, 'analystAssignedAt')}
              >
                Edit
              </Button>
            </Box>
          )}
        </Box>
      ))}

      {/* History Events */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>History Events</Typography>
        {sample.history?.map((event, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box>
              <Typography variant="body2">
                <Chip label={event.eventType} size="small" sx={{ mr: 1 }} />
                {formatToIST(event.at)}
              </Typography>
              {event.note && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {event.note}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              startIcon={<Edit />}
              onClick={() => handleEdit('history', event)}
            >
              Edit
            </Button>
          </Box>
        ))}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Edit Timestamp</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Editing: {selectedItem?.type === 'created' ? 'Sample Creation' : 
                     selectedItem?.type === 'history' ? `${selectedItem.item?.eventType} Event` :
                     `${selectedItem?.item?.department} ${selectedItem?.field?.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="New Date & Time"
              value={newTimestamp}
              onChange={(newValue) => setNewTimestamp(newValue)}
              ampm={true}
              timezone="Asia/Kolkata"
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal'
                }
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimestampEditor;